import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { Toaster } from "sonner";

import PublicLayout from "@/components/PublicLayout";
import AdminLayout from "@/components/AdminLayout";
import { RequireUser, RequireAdmin } from "@/components/ProtectedRoute";

import Home from "@/pages/public/Home";
import Programme from "@/pages/public/Programme";
import Ateliers from "@/pages/public/Ateliers";
import AtelierDetail from "@/pages/public/AtelierDetail";
import Restauration from "@/pages/public/Restauration";
import Tickets from "@/pages/public/Tickets";
import Infos from "@/pages/public/Infos";

import Login from "@/pages/visitor/Login";
import Register from "@/pages/visitor/Register";
import Account from "@/pages/visitor/Account";
import OrderDetail from "@/pages/visitor/OrderDetail";
import Bracelet from "@/pages/visitor/Bracelet";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminReservations from "@/pages/admin/AdminReservations";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminContent from "@/pages/admin/AdminContent";
import AdminPersonalization from "@/pages/admin/AdminPersonalization";
import AdminBilletterie from "@/pages/admin/AdminBilletterie";

function ScrollToTop() {
  const loc = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  return null;
}

function PublicWrap({ children }) {
  return <PublicLayout>{children}</PublicLayout>;
}

function AdminWrap({ children, superOnly = false, menu = null }) {
  return (
    <RequireAdmin superOnly={superOnly} menu={menu}>
      <AdminLayout>{children}</AdminLayout>
    </RequireAdmin>
  );
}

// Root route: if admin logged in → dashboard, else → login page
function RootRoute() {
  const { admin } = useAuth();
  return admin ? <Navigate to="/admin" replace /> : <AdminLogin />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster theme="dark" position="top-right" richColors closeButton />
          <Routes>
            {/* Root = admin login (or dashboard if already logged in) */}
            <Route path="/" element={<RootRoute />} />

            {/* Public festival site (kept accessible via /public/*) */}
            <Route path="/public" element={<PublicWrap><Home /></PublicWrap>} />
            <Route path="/public/programme" element={<PublicWrap><Programme /></PublicWrap>} />
            <Route path="/public/ateliers" element={<PublicWrap><Ateliers /></PublicWrap>} />
            <Route path="/public/ateliers/:id" element={<PublicWrap><AtelierDetail /></PublicWrap>} />
            <Route path="/public/restauration" element={<PublicWrap><Restauration /></PublicWrap>} />
            <Route path="/public/tickets" element={<PublicWrap><Tickets /></PublicWrap>} />
            <Route path="/public/bracelet" element={<PublicWrap><Bracelet /></PublicWrap>} />
            <Route path="/public/infos" element={<PublicWrap><Infos /></PublicWrap>} />

            {/* Short public paths (legacy, still work) */}
            <Route path="/programme" element={<PublicWrap><Programme /></PublicWrap>} />
            <Route path="/ateliers" element={<PublicWrap><Ateliers /></PublicWrap>} />
            <Route path="/ateliers/:id" element={<PublicWrap><AtelierDetail /></PublicWrap>} />
            <Route path="/restauration" element={<PublicWrap><Restauration /></PublicWrap>} />
            <Route path="/tickets" element={<PublicWrap><Tickets /></PublicWrap>} />
            <Route path="/bracelet" element={<PublicWrap><Bracelet /></PublicWrap>} />
            <Route path="/infos" element={<PublicWrap><Infos /></PublicWrap>} />

            {/* Visitor auth */}
            <Route path="/login" element={<PublicWrap><Login /></PublicWrap>} />
            <Route path="/register" element={<PublicWrap><Register /></PublicWrap>} />
            <Route path="/account" element={<PublicWrap><RequireUser><Account /></RequireUser></PublicWrap>} />
            <Route path="/order/:id" element={<PublicWrap><RequireUser><OrderDetail /></RequireUser></PublicWrap>} />

            {/* Admin backoffice */}
            <Route path="/admin/login" element={<RootRoute />} />
            <Route path="/admin" element={<AdminWrap><AdminDashboard /></AdminWrap>} />
            <Route path="/admin/reservations" element={<AdminWrap menu="reservations"><AdminReservations /></AdminWrap>} />
            <Route path="/admin/billetterie" element={<AdminWrap menu="billetterie"><AdminBilletterie /></AdminWrap>} />
            <Route path="/admin/content" element={<AdminWrap menu="contenu"><AdminContent /></AdminWrap>} />
            <Route path="/admin/users" element={<AdminWrap superOnly><AdminUsers /></AdminWrap>} />
            <Route path="/admin/personalization" element={<AdminWrap superOnly><AdminPersonalization /></AdminWrap>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
